/**
 * analytics-shared/engine/widgetSchema.js
 *
 * Chart-kind metadata for dashboard widgets. Single source of truth for:
 *   - Which axes a chart type uses (X+Y, category+value, single value).
 *   - What labels to show in the settings panel (X-Axis vs Category, Y-Axis
 *     vs Value vs Measure).
 *   - Which fields are eligible for each axis (numerics for Y, categoricals
 *     for X), so the dropdowns surface only valid choices.
 *
 * Used by both the settings panel (for the dropdowns + section labels) and
 * the widget data hook (to know whether to call buildChartData vs return
 * raw records for a table).
 */

export const AGGREGATIONS = [
  { value: 'COUNT', label: 'Count', appliesTo: 'any' },
  { value: 'SUM', label: 'Sum', appliesTo: 'numeric' },
  { value: 'AVG', label: 'Average', appliesTo: 'numeric' },
  { value: 'MIN', label: 'Min', appliesTo: 'numeric' },
  { value: 'MAX', label: 'Max', appliesTo: 'numeric' },
];

// Canonical chart-kind catalog used by the settings panel + the renderer.
// `axes` describes which widget.config keys this chart uses and how the UI
// should label them.
export const CHART_KINDS = {
  bar:    { label: 'Bar',     axes: { x: 'X-Axis',    y: 'Y-Axis' } },
  line:   { label: 'Line',    axes: { x: 'X-Axis',    y: 'Y-Axis' } },
  area:   { label: 'Area',    axes: { x: 'X-Axis',    y: 'Y-Axis' } },
  pie:    { label: 'Pie',     axes: { x: 'Category',  y: 'Value' } },
  donut:  { label: 'Donut',   axes: { x: 'Category',  y: 'Value' } },
  funnel: { label: 'Funnel',  axes: { x: 'Stage',     y: 'Value' } },
  heatmap:{ label: 'Heatmap', axes: { x: 'Category',  y: 'Value' } },
  kpi:    { label: 'KPI',     axes: { y: 'Value' } }, // no category
  table:  { label: 'Table',   axes: {} }, // column picker instead of axes
};

export const isTableChart = (kind) => kind === 'table';
export const usesCategoryAxis = (kind) => Boolean(CHART_KINDS[kind]?.axes?.x);
export const usesValueAxis = (kind) => Boolean(CHART_KINDS[kind]?.axes?.y);

// Field-type buckets used by the smart axis dropdowns. Anything not in these
// lists falls through to "text" by the metadata normaliser, which still
// renders as a category-eligible field.
const CATEGORY_TYPES = new Set(['text', 'select', 'user', 'date']);
const NUMERIC_TYPES = new Set(['number', 'currency', 'percent']);

export const isNumericField = (field) => !!field && NUMERIC_TYPES.has(field.type);
export const isCategoryField = (field) => !!field && CATEGORY_TYPES.has(field.type);

/**
 * Fields eligible for the X-axis / Category selector. Anything categorical
 * shows up; numeric fields are filtered out because you almost never group
 * by a raw number (you'd bucket it first, which we don't yet support).
 */
export const eligibleCategoryFields = (fields) => (
  (fields || []).filter(isCategoryField)
);

/**
 * Fields eligible for the Y-axis / Value selector. For aggregate='COUNT'
 * the picker can be skipped (COUNT(*) doesn't need a target field) — that
 * case is handled at the call site so this list stays narrow.
 */
export const eligibleValueFields = (fields) => (
  (fields || []).filter(isNumericField)
);

/**
 * Pick sensible default x/y/aggregate from a saved report config so a
 * freshly-added widget renders something meaningful instead of an empty
 * canvas. Resolution order:
 *   1. The report's first embedded chart (`report.charts[0]`).
 *   2. The report's first groupBy field paired with the first summary
 *      ({field, aggregate}), if both exist.
 *   3. Just the first groupBy with COUNT(*).
 *   4. The object's first numeric field with COUNT.
 *   5. {} — caller still works, but renders the empty state until the
 *      user picks an X/Y manually.
 */
export function deriveWidgetConfig(report, fields = []) {
  if (!report) return {};

  const firstChart = (report.charts || [])[0];
  if (firstChart && firstChart.groupField) {
    return {
      chartType: firstChart.type || 'bar',
      xField: firstChart.groupField,
      yField: firstChart.valueField || null,
      aggregate: firstChart.agg || (firstChart.valueField ? 'SUM' : 'COUNT'),
    };
  }

  const firstGroup = (report.groupBy || [])[0];
  const summaryEntries = Object.entries(report.summaries || {});
  if (firstGroup && summaryEntries.length) {
    const [yField, aggregate] = summaryEntries[0];
    return {
      chartType: 'bar',
      xField: firstGroup,
      yField,
      aggregate: aggregate || 'SUM',
    };
  }

  if (firstGroup) {
    return {
      chartType: 'bar',
      xField: firstGroup,
      yField: null,
      aggregate: 'COUNT',
    };
  }

  // Last resort: try to find any categorical + numeric pair from the
  // object's schema so the widget at least renders something.
  const cat = eligibleCategoryFields(fields)[0];
  const num = eligibleValueFields(fields)[0];
  if (cat && num) {
    return {
      chartType: 'bar',
      xField: cat.key,
      yField: num.key,
      aggregate: 'SUM',
    };
  }
  if (cat) {
    return {
      chartType: 'bar',
      xField: cat.key,
      yField: null,
      aggregate: 'COUNT',
    };
  }
  return {};
}
